import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Loader2 } from 'lucide-react';
import { BrandCampaignStats, getBrandCampaignStats } from '../../api/payment/brand-deal.api';
import { fillDays, formatDayLabel, formatRate } from '../../utils/brand-conversion';

// Khung "Hiệu quả" (doanh nghiệp, chi tiết chiến dịch, spec 2026-10-05-brand-conversion §3) — số tổng,
// bảng chuyển đổi theo creator, biểu đồ lượt bấm/chuyển đổi theo ngày. Biểu đồ dùng react-chartjs-2
// cùng mẫu trang Cab Ads (components/ads/ad-stats-modal.tsx).

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

// Khai ngoài component cha (landmine ghi nhớ) — 1 ô số tổng.
const Metric: React.FC<{ label: string; value: string; caption?: string }> = ({ label, value, caption }) => (
  <div className="rounded-lg border ui-divider p-3">
    <div className="text-xs ui-muted">{label}</div>
    <div className="text-lg font-bold ui-text">{value}</div>
    {caption ? <div className="text-xs ui-muted">{caption}</div> : null}
  </div>
);

interface BrandCampaignPerformanceProps {
  campaignId: string;
}

const BrandCampaignPerformance: React.FC<BrandCampaignPerformanceProps> = ({ campaignId }) => {
  const [stats, setStats] = useState<BrandCampaignStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getBrandCampaignStats(campaignId)
      .then((s) => alive && setStats(s))
      .catch(() => alive && toast.error('Không tải được hiệu quả chuyển đổi.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [campaignId]);

  if (loading) {
    return (
      <div className="rounded-xl border ui-divider p-4">
        <div className="flex items-center justify-center p-6">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Final-review I2: lấp ngày trống (2 ngày có dữ liệu cách xa nhau không còn bị nối thẳng thành 1
  // đường chéo sai lệch xu hướng) + nhãn "dd/MM" đúng (BE trả `DateTime` ⇒ JSON "yyyy-MM-ddTHH:mm:ss").
  const filledDays = fillDays(stats.byDay);
  const chartLabels = filledDays.map((d) => formatDayLabel(d.date));

  return (
    <div className="rounded-xl border ui-divider p-4 space-y-3">
      <div className="font-semibold ui-text">Hiệu quả</div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Lượt bấm" value={stats.totals.clicks.toLocaleString('vi-VN')} />
        <Metric label="Chuyển đổi" value={stats.totals.conversions.toLocaleString('vi-VN')} />
        <Metric label="Tỉ lệ chuyển đổi" value={formatRate(stats.totals.conversions, stats.totals.clicks)} />
        <Metric
          label="Tổng giá trị"
          value={fmtVnd(stats.totals.valueVnd)}
          caption={`${stats.totals.browserCount} trình duyệt · ${stats.totals.serverCount} máy chủ`}
        />
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold ui-text">Theo ngày</div>
        {filledDays.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có lượt bấm hoặc chuyển đổi nào.</p>
        ) : (
          <div className="h-48">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Lượt bấm',
                    data: filledDays.map((d) => d.clicks),
                    borderColor: '#0071A6',
                    backgroundColor: 'rgba(0,113,166,0.15)',
                    tension: 0.3,
                    fill: true,
                    pointRadius: 2,
                  },
                  {
                    label: 'Chuyển đổi',
                    data: filledDays.map((d) => d.conversions),
                    borderColor: '#F59E0B',
                    backgroundColor: 'rgba(245,158,11,0.15)',
                    tension: 0.3,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { labels: { boxWidth: 12, font: { size: 11 } } } },
                scales: {
                  x: { ticks: { font: { size: 10 } } },
                  y: { beginAtZero: true, ticks: { font: { size: 10 } } },
                },
              }}
            />
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold ui-text">Theo creator</div>
        {stats.byCreator.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có creator nào có lượt bấm hoặc chuyển đổi.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead>
                <tr className="ui-muted text-xs uppercase tracking-wide">
                  <th className="pb-1.5 pr-2 font-semibold">Creator</th>
                  <th className="pb-1.5 pr-2 font-semibold">Lượt bấm</th>
                  <th className="pb-1.5 pr-2 font-semibold">Chuyển đổi</th>
                  <th className="pb-1.5 pr-2 font-semibold">Tỉ lệ</th>
                  <th className="pb-1.5 font-semibold">Giá trị</th>
                </tr>
              </thead>
              <tbody className="divide-y ui-divider">
                {stats.byCreator.map((c) => (
                  <tr key={c.creatorId} className="ui-text">
                    <td className="py-1.5 pr-2">{c.displayName || `${c.creatorId.slice(0, 8)}…`}</td>
                    <td className="py-1.5 pr-2">{c.clicks.toLocaleString('vi-VN')}</td>
                    <td className="py-1.5 pr-2">{c.conversions.toLocaleString('vi-VN')}</td>
                    <td className="py-1.5 pr-2">{formatRate(c.conversions, c.clicks)}</td>
                    <td className="py-1.5">{fmtVnd(c.valueVnd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCampaignPerformance;
